/**
 * A believable three months of history, so the app can be judged with data in
 * it. Deliberately imperfect: the current month's salary lands short, one bill
 * never shows up and a goal slips behind pace — those are the states OKANE
 * exists to surface.
 */
import { run } from '../db'
import { addMonths, monthOf, shiftMonth, today as todayISO } from '../lib/dates'
import { round2 } from '../lib/money'
import { addGoal } from './goals'
import { addPlan } from './plans'
import { addBudget } from './budgets'
import { loadTransactions } from './transactions'
import { ensureCategories, findCategory, loadCategories } from './categories'
import { loadGoals } from './goals'
import { loadPlans } from './plans'
import { loadBudgets } from './budgets'
import { loadTags } from './tags'
import { setSettings } from './settings'
import { state } from './state'
import { ensureStartedOn } from './achievements'

/** Small deterministic PRNG so the sample set looks the same every time. */
function rng(seed) {
  let a = seed
  return () => {
    a |= 0
    a = (a + 0x6d2b79f5) | 0
    let t = Math.imul(a ^ (a >>> 15), 1 | a)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

const dayIn = (month, day) => `${month}-${String(day).padStart(2, '0')}`

const GROCERS = ['Lidl', 'Rewe', 'Edeka', 'Bio Market', 'Corner shop']
const COFFEE = ['Blue Bottle', 'Kaffeehaus', 'Roasters', 'Café Nero']
const DINING = ['Ramen bar', 'Pizzeria', 'Thai kitchen', 'Sushi Go', 'Burger joint']
const SUNDRY = [
  ['Uniqlo', 'Shopping', 45],
  ['Bookshop', 'Shopping', 22],
  ['Pharmacy', 'Health', 18],
  ['Cinema', 'Fun', 14],
  ['Taxi', 'Transport', 16],
  ['Hardware store', 'Home', 31],
]

export async function loadDemoData() {
  const today = todayISO()
  const months = [shiftMonth(monthOf(today), -2), shiftMonth(monthOf(today), -1), monthOf(today)]
  const rand = rng(20260905)
  const pick = (arr) => arr[Math.floor(rand() * arr.length)]
  const between = (lo, hi) => round2(lo + rand() * (hi - lo))

  // Due dates are pinned relative to today so the sample always tells its
  // story: whatever the date, some bills have already come and gone.
  const dom = Number(today.slice(8, 10))
  const justGone = (offset) => Math.min(28, Math.max(1, dom - offset))
  const DAY = {
    salary: dom >= 26 ? 25 : justGone(1),
    rent: 1,
    electricity: justGone(2),
    streaming: justGone(3),
    internet: 8,
    phone: 15,
    gym: 5,
  }

  await setSettings({ openingBalance: 1450, onboarded: true })

  const salary = await addPlan({
    type: 'income',
    amount: 3200,
    cadence: 'monthly',
    start_date: dayIn(months[0], DAY.salary),
    category: 'Income',
    tolerance: 0.02,
  })
  // A plan is its category, so each of these is a category of its own rather
  // than three different things all filed under "Utilities".
  const bills = [
    ['Rent', 1150, DAY.rent, 0.02],
    ['Electricity', 85, DAY.electricity, 0.3],
    ['Internet', 39.99, DAY.internet, 0.05],
    ['Phone', 19.99, DAY.phone, 0.05],
    ['Gym', 34.9, DAY.gym, 0.05],
    ['Streaming', 17.99, DAY.streaming, 0.1],
  ]
  const planIds = {}
  for (const [category, amount, day, tolerance] of bills) {
    const p = await addPlan({
      type: 'expense',
      amount,
      cadence: 'monthly',
      start_date: dayIn(months[0], day),
      category,
      tolerance,
    })
    planIds[category] = p.id
  }
  await addPlan({
    type: 'expense',
    amount: 240,
    cadence: 'quarterly',
    start_date: dayIn(months[0], 10),
    category: 'Insurance',
    tolerance: 0.05,
  })

  // Groceries are not a bill on the 3rd: there is no day to expect them on,
  // only a figure the month should come to. That is a budget, not a plan.
  await addBudget({
    type: 'expense',
    amount: 420,
    cadence: 'monthly',
    start_date: dayIn(months[0], 1),
    categories: ['Groceries'],
    tolerance: 0.15,
  })

  // Backdated so both goals have already set part of themselves aside.
  await addGoal({
    name: 'Japan trip',
    target_amount: 4000,
    initial_amount: 600,
    pace_mode: 'by_date',
    target_date: addMonths(today, 10),
    cadence: 'monthly',
    start_date: `${months[0]}-01`,
    priority: 2,
  })
  await addGoal({
    name: 'Emergency fund',
    target_amount: 6000,
    initial_amount: 1500,
    pace_mode: 'by_amount',
    monthly_amount: 100,
    cadence: 'fortnightly',
    start_date: `${months[1]}-01`,
    priority: 1,
  })

  const tx = []
  const push = (date, description, amount, category, extra = {}) => {
    if (date > today) return
    tx.push({ date, description, amount: Math.abs(amount), category, ...extra })
  }

  months.forEach((month, index) => {
    const isCurrent = index === months.length - 1

    // Income — the current month deliberately lands short of the 3,200 plan.
    push(dayIn(month, DAY.salary), 'ACME Corp payroll', isCurrent ? 2980 : 3200, 'Income', {
      kind: 'income',
    })
    if (!isCurrent && rand() > 0.5)
      push(dayIn(month, 14), 'Freelance invoice', between(180, 420), 'Side income', {
        kind: 'income',
      })

    // Each of these wears the category of the plan that expects it.
    push(dayIn(month, DAY.rent), 'Monthly rent — landlord', 1150, 'Rent')
    // Electricity never arrives in the current month: that is the missing-bill case.
    if (!isCurrent)
      push(dayIn(month, DAY.electricity), 'Stadtwerke energy', between(72, 108), 'Electricity')
    push(dayIn(month, DAY.internet), 'Fibre internet', 39.99, 'Internet')
    push(dayIn(month, DAY.phone), 'Mobile phone', 19.99, 'Phone')
    push(dayIn(month, DAY.gym), 'Fitness gym', 34.9, 'Gym')
    // Streaming quietly went up in price — the amount-drift case.
    push(dayIn(month, DAY.streaming), 'Netflix + Spotify', isCurrent ? 22.99 : 17.99, 'Streaming')
    if (index === 0) push(dayIn(month, 10), 'Allianz insurance', 240, 'Insurance')

    for (let day = 2; day <= 28; day += 3)
      push(dayIn(month, day), pick(GROCERS), between(18, 74), 'Groceries')
    for (let day = 3; day <= 27; day += 4)
      push(dayIn(month, day), pick(COFFEE), between(2.8, 6.4), 'Coffee')
    for (let day = 6; day <= 26; day += 7)
      push(dayIn(month, day), pick(DINING), between(16, 52), 'Dining out')
    push(dayIn(month, 4), 'Transit pass', 49, 'Transport')
    for (let i = 0; i < 3; i++) {
      const [name, category, base] = pick(SUNDRY)
      push(dayIn(month, 2 + Math.floor(rand() * 26)), name, between(base * 0.6, base * 1.5), category)
    }

  })

  // The quiet path writes rows directly, so the categories it uses are made
  // first — a row stores its category's id, and there is none until then.
  await ensureCategories(tx.map((t) => t.category))
  for (const t of tx.sort((a, b) => a.date.localeCompare(b.date))) {
    await addTransactionQuiet(t)
  }
  await loadTransactions()
  return { transactions: tx.length, salary: salary.id }
}

/** Bulk insert path: skips the per-row reload the UI does. */
async function addTransactionQuiet(input) {
  const kind = input.kind ?? 'expense'
  const amount = kind === 'income' ? Math.abs(input.amount) : -Math.abs(input.amount)
  const now = new Date().toISOString()
  await run(
    `INSERT INTO transactions
       (id, date, description, amount, category_id, payment_type, created_at, updated_at)
     VALUES (?, ?, ?, ?, ?, '', ?, ?)`,
    [
      `tx_${Math.random().toString(36).slice(2, 12)}`,
      input.date,
      input.description,
      round2(amount),
      findCategory(input.category)?.id ?? null,
      now,
      now,
    ],
  )
}

export async function eraseEverything() {
  // Tags and categories go too: they are yours, and "erase all" means all.
  for (const table of [
    'transactions',
    'plans',
    'budgets',
    'goals',
    'categories',
    'tags',
    'achievements',
    'settings',
  ]) {
    await run(`DELETE FROM ${table}`)
  }
  // Starting over starts the count again: nothing before today is yours to be
  // rewarded for, and what was earned went with the rest.
  state.achievements = []
  state.settings.startedOn = null
  await ensureStartedOn()
  await Promise.all([
    loadPlans(),
    loadBudgets(),
    loadGoals(),
    loadTransactions(),
    loadTags(),
    loadCategories(),
  ])
}
